import { useMemo, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { FeatureFlagOp } from '@shared';
import { Button, Card, Input, Popover, PopoverContent, PopoverTrigger, cn } from '@design-system';
import { useDebounce } from '@/hooks/useDebounce';
import { advisorAdminApi, advisorAdminKeys } from '@/features/wizard-advisor-admin/api/api';
import { getErrorMessage } from '@/features/wizard-advisor-admin/utils/get-error-message';
import {
  appConfigFeatureFlagsApi,
  appConfigFeatureFlagsKeys,
  type AppConfigClientProfile,
} from '../api/api';
import { OverrideCard, OverrideFlagRow } from './AdvisorCentricView';

const emptyClientProfiles: AppConfigClientProfile[] = [];
const MIN_CLIENT_SEARCH_LENGTH = 2;
const ADVISOR_ACCOUNT_ACTIVATION_FLAG = 'advisorAccountActivation';

type ClientCentricViewProps = {
  originalConfig: AppConfigSchema;
  targetConfig: AppConfigSchema;
  onEnqueueOp: (operation: FeatureFlagOp) => void;
  isSaving: boolean;
};

export function ClientCentricView({
  originalConfig,
  targetConfig,
  onEnqueueOp,
  isSaving,
}: ClientCentricViewProps) {
  const clientIds = useMemo(
    () =>
      Object.entries(targetConfig.clients ?? {})
        .filter(([, flags]) => typeof flags?.[ADVISOR_ACCOUNT_ACTIVATION_FLAG] === 'boolean')
        .map(([clientId]) => clientId)
        .sort(),
    [targetConfig],
  );
  const lookups = useClientFeatureFlagBatchLookup(clientIds);
  const lookupByClientId = useMemo(
    () => new Map(lookups.map(lookup => [lookup.clientId, lookup])),
    [lookups],
  );

  return (
    <div className="max-w-4xl space-y-2">
      <div className="flex justify-end">
        <AddClientPopover
          existingClientIds={clientIds}
          onAdd={client =>
            onEnqueueOp({
              type: 'SET_CLIENT_FLAG',
              clientId: client.clientId,
              flagKey: ADVISOR_ACCOUNT_ACTIVATION_FLAG,
              value: true,
            })
          }
          disabled={isSaving}
        />
      </div>

      {clientIds.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground text-sm">No client overrides in current AppConfig.</p>
        </Card>
      )}

      {clientIds.map(clientId => {
        const lookup = lookupByClientId.get(clientId);
        return (
          <ClientOverrideCard
            key={clientId}
            clientId={clientId}
            client={lookup?.client ?? null}
            isLookupPending={lookup?.isPending ?? false}
            value={targetConfig.clients?.[clientId]?.[ADVISOR_ACCOUNT_ACTIVATION_FLAG] === true}
            isPending={
              originalConfig.clients?.[clientId]?.[ADVISOR_ACCOUNT_ACTIVATION_FLAG] !==
              targetConfig.clients?.[clientId]?.[ADVISOR_ACCOUNT_ACTIVATION_FLAG]
            }
            onChange={value =>
              onEnqueueOp({
                type: 'SET_CLIENT_FLAG',
                clientId,
                flagKey: ADVISOR_ACCOUNT_ACTIVATION_FLAG,
                value,
              })
            }
            isSaving={isSaving}
          />
        );
      })}
    </div>
  );
}

function ClientOverrideCard({
  clientId,
  client,
  isLookupPending,
  value,
  isPending,
  onChange,
  isSaving,
}: {
  clientId: string;
  client: Client | null;
  isLookupPending: boolean;
  value: boolean;
  isPending: boolean;
  onChange: (value: boolean) => void;
  isSaving: boolean;
}) {
  const fullName = client
    ? [client.clientFirstName, client.clientLastName].filter(Boolean).join(' ')
    : '';

  return (
    <OverrideCard
      scopeId={clientId}
      displayName={fullName}
      email={client?.clientEmail}
      isLookupPending={isLookupPending}
      overrideCount={1}
    >
      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
        <OverrideFlagRow
          flagKey={ADVISOR_ACCOUNT_ACTIVATION_FLAG}
          value={value}
          isPending={isPending}
          disabled={isSaving}
          onChange={onChange}
          ariaLabel="Advisor account activation"
        />
      </div>
    </OverrideCard>
  );
}

function AddClientPopover({
  existingClientIds,
  onAdd,
  disabled,
}: {
  existingClientIds: readonly string[];
  onAdd: (client: AppConfigClientProfile) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [emailQuery, setEmailQuery] = useState('');
  const debouncedEmailQuery = useDebounce(emailQuery, 300).trim();
  const search = useClientFeatureFlagSearch(debouncedEmailQuery);
  const existingClientIdSet = useMemo(() => new Set(existingClientIds), [existingClientIds]);
  const isSearchable = emailQuery.trim().length >= MIN_CLIENT_SEARCH_LENGTH;
  const isWaitingForDebounce = emailQuery.trim() !== debouncedEmailQuery;

  function resetAndClose() {
    setOpen(false);
    setEmailQuery('');
  }

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) setEmailQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add client
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[28rem] p-2"
      >
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            autoFocus
            value={emailQuery}
            onChange={event => setEmailQuery(event.target.value)}
            placeholder="Search by client email…"
            className="h-9 pl-8"
            aria-label="Client email"
          />
        </div>

        <div className="mt-2 max-h-72 overflow-auto">
          {!isSearchable ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              Type at least {MIN_CLIENT_SEARCH_LENGTH} characters.
            </p>
          ) : isWaitingForDebounce || search.isFetching ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 px-2 py-4 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching clients…
            </div>
          ) : search.isError ? (
            <p className="px-2 py-4 text-center text-xs text-red-700">
              {getErrorMessage(search.error, 'Unable to search client profiles')}
            </p>
          ) : search.clients.length === 0 ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              No matching client profiles.
            </p>
          ) : (
            search.clients.map(client => {
              const fullName = [client.clientFirstName, client.clientLastName]
                .filter(Boolean)
                .join(' ');
              const alreadyAdded = existingClientIdSet.has(client.clientId);
              return (
                <button
                  key={client.clientId}
                  type="button"
                  disabled={alreadyAdded}
                  className={cn(
                    'w-full rounded px-2 py-2 text-left',
                    alreadyAdded
                      ? 'cursor-not-allowed bg-gray-50 opacity-60'
                      : 'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                  )}
                  onClick={() => {
                    onAdd(client);
                    resetAndClose();
                  }}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">
                      {fullName || 'Unnamed client'}
                    </span>
                    {alreadyAdded && (
                      <span className="text-muted-foreground text-[10px]">Added</span>
                    )}
                  </span>
                  <span className="text-muted-foreground block truncate text-xs">
                    {client.clientEmail}
                  </span>
                  <span className="text-muted-foreground block font-mono text-[10px]">
                    {client.clientId}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function useClientFeatureFlagSearch(normalizedEmailQuery: string) {
  const query = useQuery({
    queryKey: [...appConfigFeatureFlagsKeys.all, 'clients', 'search', normalizedEmailQuery],
    queryFn: () => appConfigFeatureFlagsApi.searchClients(normalizedEmailQuery),
    enabled: normalizedEmailQuery.length >= MIN_CLIENT_SEARCH_LENGTH,
    staleTime: 10_000,
    retry: false,
  });

  return {
    clients: query.data?.clients ?? emptyClientProfiles,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}

function useClientFeatureFlagBatchLookup(clientIds: readonly string[]) {
  const queries = useQueries({
    queries: clientIds.map(clientId => ({
      queryKey: [...advisorAdminKeys.all, 'appConfigClient', clientId] as const,
      queryFn: () => advisorAdminApi.searchClient({ clientId }),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  return clientIds.map((clientId, index) => ({
    clientId,
    client: queries[index].data?.client ?? null,
    advisor: queries[index].data?.advisor ?? null,
    isPending: queries[index].isPending,
  }));
}
